// Analytics collegata a tutti gli endpoint reali del backend
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, AreaChart, Area, LineChart, Line,
} from 'recharts'
import { Download, Calendar, AlertTriangle, Clock, TrendingUp } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Select } from '../../components/ui/Select'
import { Badge } from '../../components/ui/Badge'
import { Tabs, TabsContent } from '../../components/ui/Tabs'
import { ContextualHelp } from '../../components/shared/ContextualHelp'
import api from '../../lib/api'

// ----------------------------------------------------------------
// Tipi risposta backend
// ----------------------------------------------------------------
interface KpiSummary {
  totalTicketsMtd: number
  avgResolutionHours: number
  slaCompliancePercent: number
  totalRevenueMtd: number
}

interface TechProductivity {
  technicianId: string
  firstName: string
  lastName: string
  interventionsCount: number
  interventionsPerDay: number
  workHours: number
  travelHours: number
  totalKm: number
  slaRespectPercent: number
  signedReports: number
  unsignedReports: number
}

interface SLATrend { month: string; respected: number; violated: number }
interface SLAClient { companyId: string; companyName: string; respected: number; violated: number; compliancePercent: number }
interface SLABreach { ticketId: string; ticketNumber: string; slaDeadline: string; hoursRemaining: number }

interface MaterialConsumption {
  materialId: string
  materialName: string
  category: string | null
  unit: string
  totalQty: number
  totalCost: number
}
interface LowStock { materialId: string; materialName: string; currentQty: number; minQty: number; deficit: number }

interface RevenueByClient { companyId: string; companyName: string; monthlyValue: number; totalRevenue: number }
interface MarginByContract { contractId: string; contractNumber: string; revenue: number; cost: number; margin: number; marginPercent: number }

// ----------------------------------------------------------------
// Costanti
// ----------------------------------------------------------------
const PERIOD_OPTIONS = [
  { value: 'month',      label: 'Questo mese' },
  { value: 'last_month', label: 'Mese scorso' },
  { value: 'quarter',    label: 'Trimestre' },
  { value: 'year',       label: 'Anno' },
]

const TABS = [
  { value: 'productivity', label: 'Produttività' },
  { value: 'sla',          label: 'SLA' },
  { value: 'materials',    label: 'Materiali' },
  { value: 'economic',     label: 'Economico' },
]

const TOOLTIP_STYLE = {
  backgroundColor: 'var(--fc-surface)',
  border: '1px solid var(--fc-border)',
  borderRadius: 'var(--fc-radius)',
  fontSize: '12px',
}

function getPeriodRange(p: string): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  if (p === 'month')      from.setMonth(from.getMonth() - 1)
  else if (p === 'last_month') { from.setMonth(from.getMonth() - 2); to.setMonth(to.getMonth() - 1) }
  else if (p === 'quarter') from.setMonth(from.getMonth() - 3)
  else if (p === 'year')  from.setFullYear(from.getFullYear() - 1)
  return { from: from.toISOString(), to: to.toISOString() }
}

// ----------------------------------------------------------------
// Componente principale
// ----------------------------------------------------------------
export function AnalyticsPage() {
  const [period, setPeriod] = useState('month')
  const [tab, setTab] = useState('productivity')

  const range = getPeriodRange(period)

  // KPI summary (sempre, non dipende dal periodo — mostra MTD)
  const { data: kpi } = useQuery<KpiSummary>({
    queryKey: ['analytics', 'kpi'],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: KpiSummary }>('/analytics/kpi-summary')
      return res.data.data
    },
  })

  // Produttività tecnici
  const { data: productivity, isLoading: prodLoading } = useQuery<TechProductivity[]>({
    queryKey: ['analytics', 'productivity', period],
    queryFn: async () => {
      try {
        const res = await api.get<{ success: boolean; data: { technicians: TechProductivity[] } }>(
          '/analytics/productivity', { params: range }
        )
        return res.data.data.technicians ?? []
      } catch { return [] }
    },
    enabled: tab === 'productivity',
  })

  // SLA
  const { data: sla, isLoading: slaLoading } = useQuery<{
    trend: SLATrend[]
    byClient: SLAClient[]
    upcomingBreaches: SLABreach[]
  }>({
    queryKey: ['analytics', 'sla', period],
    queryFn: async () => {
      try {
        const res = await api.get<{ success: boolean; data: { trend: SLATrend[]; byClient: SLAClient[]; upcomingBreaches: SLABreach[] } }>(
          '/analytics/sla', { params: range }
        )
        return res.data.data
      } catch { return { trend: [], byClient: [], upcomingBreaches: [] } }
    },
    enabled: tab === 'sla',
  })

  // Materiali
  const { data: materials, isLoading: matLoading } = useQuery<{
    consumption: MaterialConsumption[]
    lowStock: LowStock[]
  }>({
    queryKey: ['analytics', 'materials', period],
    queryFn: async () => {
      try {
        const res = await api.get<{ success: boolean; data: { consumption: MaterialConsumption[]; lowStock: LowStock[] } }>(
          '/analytics/materials', { params: range }
        )
        return res.data.data
      } catch { return { consumption: [], lowStock: [] } }
    },
    enabled: tab === 'materials',
  })

  // Economico (solo admin — 403 gestito silenziosamente)
  const { data: economic, isLoading: ecoLoading, isError: ecoError } = useQuery<{
    revenueByClient: RevenueByClient[]
    marginByContract: MarginByContract[]
  }>({
    queryKey: ['analytics', 'economic', period],
    queryFn: async () => {
      const res = await api.get<{ success: boolean; data: { revenueByClient: RevenueByClient[]; marginByContract: MarginByContract[] } }>(
        '/analytics/economic', { params: range }
      )
      return res.data.data
    },
    enabled: tab === 'economic',
    retry: false,
  })

  // Export
  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    try {
      const res = await api.get('/analytics/export/operational', {
        params: { ...range, format },
        responseType: 'blob',
      })
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `analytics-${period}.${format}`
      a.click()
      URL.revokeObjectURL(url)
    } catch { /* ignora */ }
  }

  // ----------------------------------------------------------------
  // Render
  // ----------------------------------------------------------------
  return (
    <div className="space-y-6">
      <PageHeader
        title="Analytics"
        subtitle="Analisi operativa in tempo reale"
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" leftIcon={<Download className="h-4 w-4" />} onClick={() => void handleExport('pdf')}>PDF</Button>
            <Button variant="secondary" size="sm" leftIcon={<Download className="h-4 w-4" />} onClick={() => void handleExport('excel')}>Excel</Button>
            <Button variant="secondary" size="sm" leftIcon={<Download className="h-4 w-4" />} onClick={() => void handleExport('csv')}>CSV</Button>
          </div>
        }
      />

      {/* Filtro periodo */}
      <div className="flex items-center gap-3">
        <Calendar className="h-4 w-4 text-[var(--fc-foreground-muted)]" aria-hidden="true" />
        <div className="w-44">
          <Select options={PERIOD_OPTIONS} value={period} onValueChange={setPeriod} />
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { label: 'Ticket (mese)', value: kpi?.totalTicketsMtd ?? '—', icon: <TrendingUp className="h-4 w-4" /> },
          { label: 'Tempo medio chiusura', value: kpi ? `${kpi.avgResolutionHours.toFixed(1)} h` : '—', icon: <Clock className="h-4 w-4" /> },
          { label: 'Rispetto SLA', value: kpi ? `${kpi.slaCompliancePercent}%` : '—', icon: <TrendingUp className="h-4 w-4" /> },
          { label: 'Ricavi contratti attivi/mese', value: kpi ? `€${kpi.totalRevenueMtd.toLocaleString('it-IT', { maximumFractionDigits: 0 })}` : '—', icon: <TrendingUp className="h-4 w-4" /> },
        ].map(({ label, value, icon }) => (
          <Card key={label} className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-xs text-[var(--fc-foreground-muted)]">
              {icon}
              {label}
            </div>
            <p className="text-2xl font-bold text-[var(--fc-foreground)]">{value}</p>
          </Card>
        ))}
      </div>

      {/* Grafici per tab */}
      <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] overflow-hidden">
        <Tabs tabs={TABS} value={tab} onValueChange={setTab}>

          {/* ---- PRODUTTIVITÀ ---- */}
          <TabsContent value="productivity" className="p-4 space-y-4">
            <Card>
              <CardHeader><CardTitle>Interventi per tecnico</CardTitle></CardHeader>
              {prodLoading ? (
                <p className="text-sm text-[var(--fc-foreground-muted)] p-4">Caricamento...</p>
              ) : (productivity?.length ?? 0) === 0 ? (
                <p className="text-sm text-[var(--fc-foreground-muted)] p-4">Nessun dato nel periodo selezionato.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={(productivity ?? []).map(t => ({
                    name: `${t.firstName} ${t.lastName}`,
                    Interventi: t.interventionsCount,
                    'Ore lavoro': parseFloat(t.workHours.toFixed(1)),
                    'Ore viaggio': parseFloat(t.travelHours.toFixed(1)),
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--fc-border)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend />
                    <Bar dataKey="Interventi" fill="var(--fc-primary)" radius={[2,2,0,0]} />
                    <Bar dataKey="Ore lavoro" fill="var(--fc-success)" radius={[2,2,0,0]} />
                    <Bar dataKey="Ore viaggio" fill="var(--fc-warning)" radius={[2,2,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            {/* Tabella KPI tecnici */}
            {(productivity?.length ?? 0) > 0 && (
              <Card>
                <CardHeader><CardTitle>KPI per tecnico</CardTitle></CardHeader>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--fc-border)] text-left text-xs text-[var(--fc-foreground-muted)]">
                        <th className="px-3 py-2">Tecnico</th>
                        <th className="px-3 py-2 text-right">Interventi</th>
                        <th className="px-3 py-2 text-right">Int/giorno</th>
                        <th className="px-3 py-2 text-right">Ore lavoro</th>
                        <th className="px-3 py-2 text-right">Km</th>
                        <th className="px-3 py-2 text-right">SLA %</th>
                        <th className="px-3 py-2 text-right">Rapportini firmati</th>
                        <th className="px-3 py-2 text-right">Non firmati</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(productivity ?? []).map(t => (
                        <tr key={t.technicianId} className="border-b border-[var(--fc-border)] hover:bg-[var(--fc-surface-raised)]">
                          <td className="px-3 py-2 font-medium">{t.firstName} {t.lastName}</td>
                          <td className="px-3 py-2 text-right">{t.interventionsCount}</td>
                          <td className="px-3 py-2 text-right">{t.interventionsPerDay}</td>
                          <td className="px-3 py-2 text-right">{t.workHours.toFixed(1)}</td>
                          <td className="px-3 py-2 text-right">{t.totalKm.toFixed(0)}</td>
                          <td className="px-3 py-2 text-right">
                            <Badge variant={t.slaRespectPercent >= 90 ? 'success' : t.slaRespectPercent >= 70 ? 'warning' : 'danger'}>
                              {t.slaRespectPercent}%
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right text-[var(--fc-success)]">{t.signedReports}</td>
                          <td className="px-3 py-2 text-right text-[var(--fc-danger)]">{t.unsignedReports}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ---- SLA ---- */}
          <TabsContent value="sla" className="p-4 space-y-4">
            {/* Alert breach imminenti */}
            {(sla?.upcomingBreaches?.length ?? 0) > 0 && (
              <div className="rounded-lg border border-[var(--fc-warning)] bg-[color-mix(in_srgb,var(--fc-warning)_8%,transparent)] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-[var(--fc-warning)]" />
                  <span className="text-sm font-semibold text-[var(--fc-foreground)]">
                    {sla!.upcomingBreaches.length} ticket con SLA in scadenza nelle prossime 24h
                  </span>
                </div>
                <div className="space-y-1">
                  {sla!.upcomingBreaches.map(b => (
                    <div key={b.ticketId} className="flex items-center justify-between text-xs">
                      <span className="font-mono text-[var(--fc-foreground-muted)]">#{b.ticketNumber}</span>
                      <span className="text-[var(--fc-warning)] font-medium">
                        {b.hoursRemaining.toFixed(1)} h rimanenti
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Card>
              <CardHeader><CardTitle>Trend SLA mensile (rispettati vs violati)</CardTitle></CardHeader>
              {slaLoading ? (
                <p className="text-sm text-[var(--fc-foreground-muted)] p-4">Caricamento...</p>
              ) : (sla?.trend?.length ?? 0) === 0 ? (
                <p className="text-sm text-[var(--fc-foreground-muted)] p-4">Nessun dato nel periodo selezionato.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={(sla?.trend ?? []).map(t => ({
                    month: t.month,
                    Rispettati: t.respected,
                    Violati: t.violated,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--fc-border)" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend />
                    <Bar dataKey="Rispettati" fill="var(--fc-success)" radius={[2,2,0,0]} />
                    <Bar dataKey="Violati" fill="var(--fc-danger)" radius={[2,2,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            {/* SLA per cliente */}
            {(sla?.byClient?.length ?? 0) > 0 && (
              <Card>
                <CardHeader><CardTitle>Compliance SLA per cliente</CardTitle></CardHeader>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--fc-border)] text-left text-xs text-[var(--fc-foreground-muted)]">
                        <th className="px-3 py-2">Cliente</th>
                        <th className="px-3 py-2 text-right">Rispettati</th>
                        <th className="px-3 py-2 text-right">Violati</th>
                        <th className="px-3 py-2 text-right">Compliance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(sla?.byClient ?? []).map(c => (
                        <tr key={c.companyId} className="border-b border-[var(--fc-border)] hover:bg-[var(--fc-surface-raised)]">
                          <td className="px-3 py-2 font-medium">{c.companyName}</td>
                          <td className="px-3 py-2 text-right text-[var(--fc-success)]">{c.respected}</td>
                          <td className="px-3 py-2 text-right text-[var(--fc-danger)]">{c.violated}</td>
                          <td className="px-3 py-2 text-right">
                            <Badge variant={c.compliancePercent >= 90 ? 'success' : c.compliancePercent >= 70 ? 'warning' : 'danger'}>
                              {c.compliancePercent}%
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ---- MATERIALI ---- */}
          <TabsContent value="materials" className="p-4 space-y-4">
            {/* Alert sotto-scorta */}
            {(materials?.lowStock?.length ?? 0) > 0 && (
              <div className="rounded-lg border border-[var(--fc-danger)] bg-[color-mix(in_srgb,var(--fc-danger)_8%,transparent)] p-3">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-[var(--fc-danger)]" />
                  <span className="text-sm font-semibold text-[var(--fc-foreground)]">
                    {materials!.lowStock.length} materiali sotto-scorta
                  </span>
                </div>
                <div className="space-y-1">
                  {materials!.lowStock.map(l => (
                    <div key={l.materialId} className="flex items-center justify-between text-xs">
                      <span className="font-medium">{l.materialName}</span>
                      <span className="text-[var(--fc-danger)]">
                        {l.currentQty} / {l.minQty} (deficit: {l.deficit})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Card>
              <CardHeader><CardTitle>Consumo materiali per periodo (top 15)</CardTitle></CardHeader>
              {matLoading ? (
                <p className="text-sm text-[var(--fc-foreground-muted)] p-4">Caricamento...</p>
              ) : (materials?.consumption?.length ?? 0) === 0 ? (
                <p className="text-sm text-[var(--fc-foreground-muted)] p-4">Nessun consumo registrato nel periodo.</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={(materials?.consumption ?? []).slice(0, 15).map(m => ({
                      name: m.materialName.length > 20 ? m.materialName.slice(0, 18) + '…' : m.materialName,
                      'Q.tà usata': m.totalQty,
                      'Costo (€)': m.totalCost,
                    }))}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--fc-border)" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} />
                    <Legend />
                    <Bar dataKey="Q.tà usata" fill="var(--fc-primary)" radius={[0,2,2,0]} />
                    <Bar dataKey="Costo (€)" fill="var(--fc-warning)" radius={[0,2,2,0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            {/* Tabella consumo dettagliata */}
            {(materials?.consumption?.length ?? 0) > 0 && (
              <Card>
                <CardHeader><CardTitle>Dettaglio consumo</CardTitle></CardHeader>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--fc-border)] text-left text-xs text-[var(--fc-foreground-muted)]">
                        <th className="px-3 py-2">Materiale</th>
                        <th className="px-3 py-2">Categoria</th>
                        <th className="px-3 py-2 text-right">Q.tà usata</th>
                        <th className="px-3 py-2 text-right">Costo totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(materials?.consumption ?? []).map(m => (
                        <tr key={m.materialId} className="border-b border-[var(--fc-border)] hover:bg-[var(--fc-surface-raised)]">
                          <td className="px-3 py-2 font-medium">{m.materialName}</td>
                          <td className="px-3 py-2 text-[var(--fc-foreground-muted)]">{m.category ?? '—'}</td>
                          <td className="px-3 py-2 text-right">{m.totalQty} {m.unit}</td>
                          <td className="px-3 py-2 text-right font-semibold">€{m.totalCost.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ---- ECONOMICO ---- */}
          <TabsContent value="economic" className="p-4 space-y-4">
            {ecoError ? (
              <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface-raised)] p-6 text-center">
                <p className="text-sm text-[var(--fc-foreground-muted)]">
                  Accesso ai dati economici riservato agli amministratori.
                </p>
              </div>
            ) : ecoLoading ? (
              <p className="text-sm text-[var(--fc-foreground-muted)] p-4">Caricamento...</p>
            ) : (
              <>
                <Card>
                  <CardHeader><CardTitle>Ricavi per cliente</CardTitle></CardHeader>
                  {(economic?.revenueByClient?.length ?? 0) === 0 ? (
                    <p className="text-sm text-[var(--fc-foreground-muted)] p-4">Nessun contratto attivo nel periodo.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={(economic?.revenueByClient ?? []).map(r => ({
                        name: r.companyName.length > 18 ? r.companyName.slice(0, 16) + '…' : r.companyName,
                        'Ricavo mensile': r.monthlyValue,
                        'Ricavo totale': r.totalRevenue,
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--fc-border)" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `€${v.toLocaleString('it-IT', { maximumFractionDigits: 0 })}`} />
                        <Tooltip
                          contentStyle={TOOLTIP_STYLE}
                          formatter={(v: number) => `€${v.toLocaleString('it-IT', { minimumFractionDigits: 2 })}`}
                        />
                        <Legend />
                        <Bar dataKey="Ricavo mensile" fill="var(--fc-primary)" radius={[2,2,0,0]} />
                        <Bar dataKey="Ricavo totale" fill="var(--fc-success)" radius={[2,2,0,0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Card>

                {/* Margine per contratto */}
                {(economic?.marginByContract?.length ?? 0) > 0 && (
                  <Card>
                    <CardHeader><CardTitle>Margine per contratto</CardTitle></CardHeader>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[var(--fc-border)] text-left text-xs text-[var(--fc-foreground-muted)]">
                            <th className="px-3 py-2">Contratto</th>
                            <th className="px-3 py-2 text-right">Ricavi</th>
                            <th className="px-3 py-2 text-right">Costi</th>
                            <th className="px-3 py-2 text-right">Margine</th>
                            <th className="px-3 py-2 text-right">Margine %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(economic?.marginByContract ?? []).map(c => (
                            <tr key={c.contractId} className="border-b border-[var(--fc-border)] hover:bg-[var(--fc-surface-raised)]">
                              <td className="px-3 py-2 font-mono text-xs">{c.contractNumber}</td>
                              <td className="px-3 py-2 text-right">€{c.revenue.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                              <td className="px-3 py-2 text-right">€{c.cost.toLocaleString('it-IT', { minimumFractionDigits: 2 })}</td>
                              <td className={`px-3 py-2 text-right font-semibold ${c.margin >= 0 ? 'text-[var(--fc-success)]' : 'text-[var(--fc-danger)]'}`}>
                                €{c.margin.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <Badge variant={c.marginPercent >= 30 ? 'success' : c.marginPercent >= 10 ? 'warning' : 'danger'}>
                                  {c.marginPercent.toFixed(1)}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

        </Tabs>
      </div>

      <ContextualHelp />
    </div>
  )
}
